/**
 * useOnDemandLookupLabels — key→label pairs for a reference table too big to
 * cache, resolved per visible grid window and never persisted.
 *
 * The cached pipeline (`useLookupLabelMaps` → `useLookupLabelSelfHeal`) reads
 * a target table's WHOLE (capped) pair set into MetaInfo. That is the right
 * shape for a 12-row `statuses` table and the wrong shape for a 200k-row
 * `Customers`: the cap truncates, the labels are wrong-by-omission, and the
 * pairs land in an exportable file nobody wanted to share. A target flagged
 * `mode: 'on-demand'` leaves that pipeline entirely (see
 * `resolveLookupPairsMode`) and is served here instead: per settled window,
 * the distinct keys the user can actually SEE are diffed against a shared
 * in-memory accumulator and only the unknown ones are fetched, through the
 * existing Table Explorer row path — one `IN (...)` per batch, no new IPC.
 *
 * Every DECISION (what is unknown, how it chunks, what a miss means, how long
 * a failed target rests) lives in the pure `utils/lookupBatchResolver.ts`;
 * this hook is the React shell around it — debounce, concurrency, visibility,
 * and the `LookupLabelInfo` output both grids already consume.
 *
 * Nothing here is persisted, on purpose. `sampledAt: undefined` is the signal
 * the cell tooltip reads to omit its "cached …" clause: these labels were read
 * live, moments ago.
 *
 * Mounted by BOTH grids (CLAUDE.md § "Two Results Grids") at the same call
 * sites as `useLookupLabelSelfHeal`, with the same visible-window rows.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { DatabaseMetaInfo, EnumLookupTarget } from '@/types/electron';
import { createExecutionId } from '@/types/queryExecution';
import { fetchTableExplorerRows } from '../../table-explorer/services/tableExplorerApi';
import {
  resolveEffectiveLookupDeclaration,
  resolveLookupPairsMode,
} from '../utils/effectiveLookupDeclaration';
import {
  accumulatorKey,
  acquireAccumulator,
  collectWindowKeys,
  createAccumulator,
  markBatchFailed,
  markInFlight,
  mergeBatchResult,
  planBatches,
  type LookupLabelAccumulator,
} from '../utils/lookupBatchResolver';
import {
  LOOKUP_TARGET_COLUMNS_STALE_MS,
  fetchLookupTargetColumns,
  lookupTargetColumnsQueryKey,
  resolveLookupLabelColumn,
} from '../utils/lookupLabelColumn';
import type { LookupLabelInfo, LookupLabelMapColumn } from './useLookupLabelMaps';
import {
  hostCacheIdentity,
  hostMetadataQueryKey,
  type TableExplorerHostLookupDeclaration,
  type TableExplorerRelationshipPort,
} from '../../table-explorer/components/tableExplorerHostPorts';

/** How long a scrolling window must stand still before its keys are fetched. */
export const ON_DEMAND_LABEL_DEBOUNCE_MS = 200;
/** Columns resolved in parallel per grid; chunks within a column stay serial. */
export const MAX_CONCURRENT_ON_DEMAND_COLUMNS = 4;

/**
 * Module-level: two grids side by side, a remount, and every column pointing
 * at the same target share ONE accumulator per
 * `(connection, database, targetSchema, targetTable, targetKeyColumn,
 * labelColumn)`, so a key resolved anywhere is never fetched again.
 * Session-lived; never written to disk, and bounded to `MAX_LOOKUP_TARGETS`
 * least-recently-used entries (`acquireAccumulator`) so a long session across
 * many databases cannot grow it without limit.
 */
const registry = new Map<string, LookupLabelAccumulator>();

/**
 * The binding published for a column that resolves NO labels — the target has
 * no label column, or its metadata could not be read. It is a real, valid
 * binding pointing at an empty snapshot, not an absent one, and that is the
 * whole point: leaving the previous binding in place would keep rendering the
 * labels resolved from a column the user has since withdrawn. Its key can
 * never collide with a live accumulator's, since a real label column is never
 * `''`.
 */
const EMPTY_ACCUMULATOR: LookupLabelAccumulator = createAccumulator();

/** Test-only: forget every resolved pair (unit tests, e2e resets). */
export function __resetOnDemandLabelRegistryForTests(): void {
  registry.clear();
}

/** Test-only: the live accumulator identities (pins the SQL key shape). */
export function __onDemandLabelRegistryKeysForTests(): string[] {
  return [...registry.keys()];
}

/** A grid column plus the row-object key its cell values are read with. */
export type OnDemandLookupColumn = LookupLabelMapColumn & { key: string };

/** One qualifying column: its grid identity and the target it resolves from. */
type OnDemandPlan = {
  id: string;
  key: string;
  target: EnumLookupTarget;
  targetFilter?: import('../../core/types/workspaceTypes').FilterGroup[];
  declarationId?: string;
  /**
   * Host-declared columns only (Azure Table, S4 review C): the declaration,
   * its JSON-safe target filter and the port's cache scope. Extends the
   * accumulator key and validates the binding, so a reconnect or a mutation
   * resolves again (M5). SQL plans never carry it — their accumulator key is
   * exactly the base key (N1).
   */
  hostIdentity?: string;
};

/**
 * What a column is currently bound to. The labels themselves live in
 * `LabelStore.snapshots`, keyed by accumulator rather than by column, so every
 * column reading the same target sees one merge (a sibling column that found
 * all its keys already in flight still renders what the fetching column
 * merged). The identity fields are what the output memo validates against the
 * CURRENT connection/database/target — a binding left over from a previous
 * database or a re-pointed FK must render nothing rather than the old labels.
 */
type Binding = {
  accumulatorKey: string;
  connectionId: string;
  database: string;
  target: EnumLookupTarget;
  /** The plan's `hostIdentity` when bound (host columns only). */
  hostIdentity?: string;
};

/** Column bindings plus the per-accumulator label snapshots they point at. */
type LabelStore = {
  bindings: ReadonlyMap<string, Binding>;
  snapshots: ReadonlyMap<string, Map<string, string | null>>;
};

const EMPTY_PLANS: OnDemandPlan[] = [];
const EMPTY_STORE: LabelStore = { bindings: new Map(), snapshots: new Map() };
const EMPTY_LABELS: Map<string, string | null> = new Map();

const sameTarget = (a: EnumLookupTarget, b: EnumLookupTarget): boolean =>
  (a.toSchema ?? '') === (b.toSchema ?? '') &&
  a.toTable === b.toTable &&
  a.toColumn === b.toColumn;

export function useOnDemandLookupLabels(args: {
  connectionId: string | null | undefined;
  database: string | null | undefined;
  metaInfo: DatabaseMetaInfo | null | undefined;
  /** Per-column resolved FK target, as both grids already memoize it. */
  fkLookupTargets?: Map<string, EnumLookupTarget>;
  hostLookupDeclarations?: Map<string, TableExplorerHostLookupDeclaration>;
  columns: readonly OnDemandLookupColumn[];
  /** The rows the grid is actually rendering (its virtualized window). */
  rows: readonly Record<string, unknown>[];
  relationshipPort?: TableExplorerRelationshipPort;
}): Map<string, LookupLabelInfo> {
  const { connectionId, database, metaInfo, fkLookupTargets, hostLookupDeclarations, columns, rows, relationshipPort } = args;
  const queryClient = useQueryClient();
  const [store, setStore] = useState<LabelStore>(EMPTY_STORE);
  const visible = useDocumentVisible();

  const hostScope = relationshipPort?.cacheScope;
  const plans = useMemo(
    () => resolveOnDemandPlans(metaInfo, columns, fkLookupTargets, hostLookupDeclarations, hostScope),
    [metaInfo, columns, fkLookupTargets, hostLookupDeclarations, hostScope],
  );

  /**
   * Bind one column to an accumulator and publish that accumulator's labels —
   * bailing out of the render when neither changed. Labels are only ever ADDED
   * to an accumulator, so a same-key, same-size snapshot is provably the same
   * snapshot: a settled window that resolved nothing costs zero renders.
   *
   * The snapshot is stored PER ACCUMULATOR, not per column: one column's merge
   * is immediately visible to every sibling column bound to the same target,
   * whichever of them actually issued the batch.
   *
   * The snapshot is copied eagerly here rather than inside the state updater —
   * accumulators are mutable and shared, and a lazily-copied "snapshot" would
   * capture whatever the accumulator held at render time instead of at publish
   * time.
   */
  const publish = useCallback(
    (columnId: string, binding: Binding, acc: LookupLabelAccumulator) => {
      const key = binding.accumulatorKey;
      const labels = new Map(acc.labels);
      setStore((prev) => {
        const bound = prev.bindings.get(columnId);
        const boundAlready =
          bound !== undefined &&
          bound.accumulatorKey === key &&
          bound.connectionId === binding.connectionId &&
          bound.database === binding.database &&
          bound.hostIdentity === binding.hostIdentity &&
          sameTarget(bound.target, binding.target);
        const snapshot = prev.snapshots.get(key);
        const snapshotUnchanged = snapshot !== undefined && snapshot.size === labels.size;
        if (boundAlready && snapshotUnchanged) return prev;
        return {
          bindings: boundAlready ? prev.bindings : new Map(prev.bindings).set(columnId, binding),
          snapshots: snapshotUnchanged ? prev.snapshots : new Map(prev.snapshots).set(key, labels),
        };
      });
    },
    [],
  );

  useEffect(() => {
    if (plans.length === 0 || rows.length === 0) return;
    if (!connectionId || !database || !visible) return;

    let cancelled = false;
    const timer = setTimeout(() => {
      const context: BatchContext = {
        queryClient,
        connectionId,
        database,
        metaInfo,
        rows,
        publish,
        isCancelled: () => cancelled,
        relationshipPort,
      };
      const queue = plans.slice();
      const workers = Array.from(
        { length: Math.min(MAX_CONCURRENT_ON_DEMAND_COLUMNS, queue.length) },
        async () => {
          for (let plan = queue.shift(); plan; plan = queue.shift()) {
            if (cancelled) return;
            await resolveColumnLabels(context, plan);
          }
        },
      );
      // Errors are swallowed per batch inside; this only keeps the pool alive.
      void Promise.all(workers);
    }, ON_DEMAND_LABEL_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `metaInfo` is read at fetch time (freshest label-column resolution) and
    // deliberately not a dependency — a MetaInfo re-read must not restart the
    // debounce of a window that is already settled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, rows, connectionId, database, visible, queryClient, publish, relationshipPort]);

  const { bindings, snapshots } = store;

  return useMemo(() => {
    const out = new Map<string, LookupLabelInfo>();
    for (const plan of plans) {
      // A binding is only readable while it still describes what this column
      // resolves from NOW. Switch database, re-point the FK, or reconnect and
      // the previous binding is treated as absent — the bare key renders until
      // the new target resolves, which is the honest state. (Nothing is
      // deleted: switching back re-validates the same binding and its snapshot
      // instantly, and the accumulator registry never re-fetches those keys.)
      const binding = bindings.get(plan.id);
      const valid =
        binding !== undefined &&
        binding.connectionId === connectionId &&
        binding.database === database &&
        binding.hostIdentity === plan.hostIdentity &&
        sameTarget(binding.target, plan.target);
      out.set(plan.id, {
        labels: (valid ? snapshots.get(binding.accumulatorKey) : undefined) ?? EMPTY_LABELS,
        target: plan.target,
        sampledAt: undefined,
        truncated: false,
      });
    }
    return out;
  }, [plans, bindings, snapshots, connectionId, database]);
}

/** Columns whose effective lookup obtains its pairs on demand. */
function resolveOnDemandPlans(
  metaInfo: DatabaseMetaInfo | null | undefined,
  columns: readonly OnDemandLookupColumn[],
  fkLookupTargets: Map<string, EnumLookupTarget> | undefined,
  hostLookupDeclarations: Map<string, TableExplorerHostLookupDeclaration> | undefined,
  hostScope: readonly (string | number)[] | undefined,
): OnDemandPlan[] {
  if (!metaInfo && !hostLookupDeclarations?.size) return EMPTY_PLANS;
  const out: OnDemandPlan[] = [];
  for (const column of columns) {
    if (!column.sourceTable || !column.sourceColumn) continue;
    const effective = resolveEffectiveLookupDeclaration({
      metaInfo,
      schema: column.sourceSchema ?? '',
      table: column.sourceTable,
      column: column.sourceColumn,
      resolvedTarget: fkLookupTargets?.get(column.id),
      hostDeclaration: hostLookupDeclarations?.get(column.id),
    });
    if (!effective) continue;
    // A declaration of any other source owns the column — its values are not
    // FK keys (same rule as `useLookupLabelMaps`).
    if (effective.kind === 'explicit' && effective.declaration.source !== 'lookup') continue;
    if (resolveLookupPairsMode(metaInfo, effective) !== 'on-demand') continue;
    const target = effective.kind === 'explicit' ? effective.declaration.lookup : effective.lookup;
    if (!target) continue;
    const host = hostLookupDeclarations?.get(column.id);
    out.push({
      id: column.id,
      key: column.key,
      target,
      ...(host
        ? {
            targetFilter: host.targetFilter,
            declarationId: host.declarationId,
            hostIdentity: hostCacheIdentity([hostScope ?? [], host.declarationId, host.targetFilter ?? []]),
          }
        : {}),
    });
  }
  return out.length === 0 ? EMPTY_PLANS : out;
}

/**
 * SQL plans: the original six-part key, byte for byte (N1). Host plans: the
 * same key extended by the declaration id and the host identity (M5).
 */
function planAccumulatorKey(connectionId: string, database: string, plan: OnDemandPlan, labelColumn: string): string {
  const { toSchema, toTable, toColumn } = plan.target;
  return plan.hostIdentity === undefined
    ? accumulatorKey(connectionId, database, toSchema ?? '', toTable, toColumn, labelColumn)
    : accumulatorKey(connectionId, database, toSchema ?? '', toTable, toColumn, labelColumn, plan.declarationId, plan.hostIdentity);
}

type BatchContext = {
  queryClient: QueryClient;
  connectionId: string;
  database: string;
  metaInfo: DatabaseMetaInfo | null | undefined;
  rows: readonly Record<string, unknown>[];
  publish: (columnId: string, binding: Binding, acc: LookupLabelAccumulator) => void;
  isCancelled: () => boolean;
  relationshipPort?: TableExplorerRelationshipPort;
};

/**
 * One column, one settled window: unknown keys → batches → merged labels.
 * Never throws — a dropped target, a revoked grant or a disconnected session
 * leaves the bare key rendered, exactly as the cached path's refresh does.
 */
async function resolveColumnLabels(ctx: BatchContext, plan: OnDemandPlan): Promise<void> {
  const windowKeys = collectWindowKeys(ctx.rows, plan.key);
  if (windowKeys.length === 0) return; // nothing visible ⇒ not one query

  const { connectionId, database } = ctx;
  const { toSchema, toTable, toColumn } = plan.target;

  /** Publish "this column resolves nothing", replacing any older binding. */
  const publishNothing = () =>
    ctx.publish(
      plan.id,
      {
        accumulatorKey: planAccumulatorKey(connectionId, database, plan, ''),
        connectionId,
        database,
        target: plan.target,
        hostIdentity: plan.hostIdentity,
      },
      EMPTY_ACCUMULATOR,
    );

  let labelColumn: string | null;
  try {
    // Every `await` below is a chance for the grid to have moved on (another
    // database, another connection, a re-pointed FK). Each one is followed by
    // a cancellation check before anything is published, so a late batch can
    // never overwrite what the CURRENT identity is showing.
    // Same query key the target picker, the declare dialog and the cached
    // self-heal use — a window reuses whatever the UI already fetched. A host
    // port (Azure Table: a guarded 1000-entity sample per listing) goes through
    // the same cache under its own scope — the `lookup-target-columns` entry
    // `lookupCacheRefresh` / `useLookupEnumLabelColumn` share (S4 review F, F7).
    const port = ctx.relationshipPort;
    const targetColumns = port
      ? await ctx.queryClient.fetchQuery({
          queryKey: hostMetadataQueryKey(port, connectionId, 'lookup-target-columns', database, toSchema, toTable),
          queryFn: async () => (await port.listColumns({
            connectionId,
            database,
            schema: toSchema ?? '',
            table: toTable,
          })).map((column) => column.name),
          staleTime: LOOKUP_TARGET_COLUMNS_STALE_MS,
        })
      : await ctx.queryClient.fetchQuery({
          queryKey: lookupTargetColumnsQueryKey(connectionId, database, toSchema, toTable),
          queryFn: () =>
            fetchLookupTargetColumns({ connectionId, database, schema: toSchema, table: toTable }),
          staleTime: LOOKUP_TARGET_COLUMNS_STALE_MS,
        });
    labelColumn = resolveLookupLabelColumn({
      metaInfo: ctx.metaInfo,
      schema: toSchema,
      table: toTable,
      keyColumn: toColumn,
      columns: targetColumns,
    });
  } catch {
    // Unreadable target metadata: no label column can be resolved, so anything
    // this column is still showing came from a resolution that no longer holds.
    if (!ctx.isCancelled()) publishNothing();
    return;
  }
  if (ctx.isCancelled()) return;
  if (!labelColumn) {
    // Keys-only target ⇒ nothing to label, nothing to fetch. Publishing the
    // empty binding is what makes withdrawing a label column (picking "(none)"
    // on the target) actually take the labels off the screen.
    publishNothing();
    return;
  }

  const key = planAccumulatorKey(connectionId, database, plan, labelColumn);
  const binding: Binding = { accumulatorKey: key, connectionId, database, target: plan.target, hostIdentity: plan.hostIdentity };
  const acc = acquireAccumulator(registry, key);
  // Adopt whatever a sibling grid already resolved for this target.
  ctx.publish(plan.id, binding, acc);

  for (const chunk of planBatches(acc, windowKeys, Date.now())) {
    if (ctx.isCancelled()) return;
    markInFlight(acc, chunk);
    try {
      if (ctx.relationshipPort) {
        const labels = await ctx.relationshipPort.resolveLabels({
          connectionId,
          database,
          table: toTable,
          keyColumn: toColumn,
          labelColumn,
          keys: chunk,
          targetFilter: plan.targetFilter,
          declarationId: plan.declarationId,
        });
        mergeBatchResult(
          acc,
          chunk,
          chunk.map((value) => ({ key: value, label: labels.get(String(value)) })),
        );
        if (!ctx.isCancelled()) ctx.publish(plan.id, binding, acc);
        continue;
      }
      const result = await fetchTableExplorerRows({
        connectionId,
        executionId: createExecutionId(`on-demand-labels-${toTable}`),
        table: { database, schema: toSchema ?? '', tableName: toTable },
        columns: [toColumn, labelColumn],
        limit: chunk.length,
        filters: [{ field: toColumn, conditions: [{ operator: 'enum', value: chunk }] }],
        sorting: [],
      });
      // A missing `rows` payload (a spilled/windowed result) is NOT an empty
      // result set — resolving the chunk to `null` off it would hide labels
      // that exist, permanently. Treat it as a failure.
      if (!result.success || !result.rows) {
        throw new Error(result.error ?? 'on-demand label batch returned no rows');
      }
      // Merge even when this resolution has been cancelled: the accumulator is
      // keyed by (connection, database, target schema/table/key column, label
      // column), so the pairs are still correct for whoever asks for them
      // next, and leaving the keys in flight would strand them permanently.
      // Only the PUBLISH is skipped.
      mergeBatchResult(
        acc,
        chunk,
        result.rows.map((row) => ({ key: row[toColumn], label: row[labelColumn] })),
      );
      if (ctx.isCancelled()) return;
      ctx.publish(plan.id, binding, acc);
    } catch {
      // The keys stay UNKNOWN (a transient failure must never masquerade as
      // "no such row"); the whole target rests for one throttle window.
      markBatchFailed(acc, chunk, Date.now());
      return;
    }
  }
}

/** `document.visibilityState`, as state — a backgrounded tab fetches nothing. */
function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(() => !isHidden());
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onChange = () => setVisible(!isHidden());
    onChange();
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

const isHidden = () => typeof document !== 'undefined' && document.hidden;
